/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useEffect, useRef } from 'react';
import Monster from 'model/monster/Monster';


interface HitPointSelectorProps {
  mon: Monster;
  onSaveHitDiceNumber: (value: number) => void;
  onSaveHitDiceType: (value: number) => void;
  onSaveHitDiceBonus: (value: number) => void;
  onSaveUsePcHitDice: (value: boolean) => void;
  onSaveHitPointsCustom: (value: string) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const HitPointSelector: React.FC<HitPointSelectorProps> = ({
  mon,
  onSaveHitDiceNumber,
  onSaveHitDiceType,
  onSaveHitDiceBonus,
  onSaveUsePcHitDice,
  onSaveHitPointsCustom,
  isOpen,
  setIsOpen,
}) => {
  const [hitDiceNumber, setHitDiceNumber] = useState<number>(mon.hitDiceNumber || 1);
  const [hitDiceType, setHitDiceType] = useState<number>(mon.hitDiceType || 8);
  const [hitDiceBonus, setHitDiceBonus] = useState<number>(mon.hitDiceBonus || 0);
  const [hitPointsCustom, setHitPointsCustom] = useState<string>(mon.hitPointsCustom || '');

  const panelRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const getHitPointsStr = (): string => {
    if (hitPointsCustom) {
      return hitPointsCustom;
    }
    return buildHitPointsStr(hitDiceNumber, hitDiceType, hitDiceBonus);
  }

  const buildHitPointsStr = (hpNum: number, hpType: number, hpBonus: number): string => {
    const hitPoints = Math.max(1, Math.round(hpNum * (hpType + 1) / 2) + hpBonus);
    let hitDiceStr = `${hpNum}d${hpType}`;
    if (hpBonus !== 0) {
      hitDiceStr += `${(hpBonus >= 0) ? '+' : ''}${hpBonus}`;
    }
    return `${hitPoints} (${hitDiceStr})`;
  }

  useEffect(() => {
    if (mon.hitDiceNumber !== hitDiceNumber) setHitDiceNumber(mon.hitDiceNumber || 1);
    if (mon.hitDiceType !== hitDiceType) setHitDiceType(mon.hitDiceType || 8);
    if (mon.hitDiceBonus !== hitDiceBonus) setHitDiceBonus(mon.hitDiceBonus || 0);
    if (mon.hitPointsCustom !== hitPointsCustom) setHitPointsCustom(mon.hitPointsCustom || '');
  }, [mon]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, attribute: string) => {
    const valueStr: string = event.target.type === 'number' ? ('' + Math.max(0, parseInt(event.target.value))) : event.target.value;
    const value = parseInt(valueStr, 10);
    if (attribute === 'hitDiceNumber') {
      setHitDiceNumber(value);
      onSaveHitDiceNumber(value);
    } else if (attribute === 'hitDiceType') {
      setHitDiceType(value);
      onSaveHitDiceType(value);
    } else if (attribute === 'hitDiceBonus') {
      setHitDiceBonus(value);
      onSaveHitDiceBonus(value);
    } else if (attribute === 'hitPointsCustom') {
      setHitPointsCustom(valueStr);
      onSaveHitPointsCustom(valueStr);
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(event.target as Node) &&
        textRef.current && !textRef.current.contains(event.target as Node) &&
        titleRef.current && !titleRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsOpen]);

  return (
    <div className="property-line">
      <h4 onMouseDown={() => setIsOpen(true)} ref={titleRef}>
        {mon.useMm2024 ? 'HP' : 'Hit Points'}
      </h4>
      &nbsp;
      <span className="multi-select-container edit-field">
        <span className="property-line" onMouseDown={() => setIsOpen(true)} ref={textRef}>
          <span className="property-line">
            {getHitPointsStr()}
          </span>
        </span>
        {isOpen && (
          <span className="multi-select-panel hp-select-panel" ref={panelRef}>
            <span className="speed-input-key">&nbsp;</span>
            <span title="Uses max roll for first level, and rounds up at each level thereafter">
              <input type="checkbox" id="pcLeveling" name="pcLeveling" required checked={mon.usePcHitDice} onChange={() => onSaveUsePcHitDice(!mon.usePcHitDice)}/>
              <label htmlFor="pcLeveling">Use PC leveling</label>
            </span>
            <br/>
            <span className="speed-input-key">Hit Dice:</span>
            <input type="number" style={{ width: '50px' }} value={hitDiceNumber} onChange={(event) => handleChange(event, 'hitDiceNumber')} />
            <select onChange={(event) => handleChange(event, 'hitDiceType')} value={hitDiceType}
              style={{ width: '50px', margin: '0px 10px 0px 10px' }} >
              <option value={4}>d4</option>
              <option value={6}>d6</option>
              <option value={8}>d8</option>
              <option value={10}>d10</option>
              <option value={12}>d12</option>
              <option value={20}>d20</option>
            </select><br />
            <span className="speed-input-key">Bonus:</span>
            <input type="number" style={{ width: '50px' }} value={hitDiceBonus} onChange={(event) => handleChange(event, 'hitDiceBonus')} /><br />
            <span className="speed-input-key">Custom:</span>
            <input style={{ width: '120px' }} value={hitPointsCustom} onChange={(event) => handleChange(event, 'hitPointsCustom')} />
          </span>
        )}
      </span>
    </div>
  );
};

export default HitPointSelector;
